import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { fetchOk } from '@atlassian/mpac-fetch-wrapper';
import { linkTemplates } from '@atlassian/mpac-utils';
// eslint-disable-next-line no-restricted-imports
import { Either } from 'funfix-core/dist/es5';
import queryString from 'query-string';
import { INSTALL_COMPLETE_JSON, REMOTE_INSTALL_JSON } from '../constants';
import { foldPaymentModel } from '../types/UPM.types';
export const configureProductName = productName => {
  switch (productName) {
    case 'jira':
      return 'Jira';
    case 'confluence':
      return 'Confluence';
    case 'compass':
      return 'Compass';
    default:
      throw new Error("Unsupported product name: ".concat(productName));
  }
};
export const buildContext = config => {
  return {
    name: configureProductName(config.product),
    type: 'application',
    id: "ari:cloud:".concat(config.product, ":").concat(config.cloudId, ":/"),
    orgId: config.orgId,
    // To understand wrokspaceAri format visit - // https://developer.atlassian.com/platform/atlassian-resource-identifier/resource-owners/registry/#ati%3Acloud%3Ajira%3Aworkspace
    workspaceAri: "ari:cloud:".concat(config.product, ":").concat(config.cloudId, ":workspace/").concat(config.activationId)
  };
};
export function returnsPromise(target, key, descriptor) {
  descriptor.value.returnsPromise = true;
  descriptor.enumerable = true;
  return descriptor;
}

/**
 * Converts an AppError thrown as a result of
 * promise rejection into into its object literal
 * form that is safely serializable across the iframe bridge
 */
export function throwsAppError(target, key, descriptor) {
  const fn = descriptor.value;

  // eslint-disable-next-line eqeqeq
  const isPromise = val => Promise.resolve(val) == val;
  descriptor.value = function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    const result = fn.apply(this, args);
    if (isPromise(result)) {
      return result.then(resolvedValue => resolvedValue).catch(error => {
        if (error.toJSON) {
          throw error.toJSON();
        } else {
          throw error;
        }
      });
    } else {
      return result;
    }
  };
  return descriptor;
}
export function unsupported(target, key, descriptor) {
  const name = descriptor.value.name;
  descriptor.value = () => Promise.reject("Marketplace SPI function [".concat(name, "] not implemented"));
  return descriptor;
}
export function instanceToModule(instance) {
  const module = {};
  const prototype = Object.getPrototypeOf(instance);
  Object.keys(prototype).forEach(key => {
    module[key] = prototype[key].bind(instance);
    module[key].returnsPromise = prototype[key].returnsPromise;
  });
  return module;
}
export async function getTokenFromUPM(upm) {
  const options = {
    retries: 2,
    retryDelay: 3000,
    credentials: getUPMCredentials(upm)
  };
  const response = await fetchOk("".concat(upm, "/rest/plugins/1.0/upm-token"), _objectSpread({
    method: 'HEAD'
  }, options));
  return response.headers.get('upm-token');
}
export async function postInstallToUPM(upm, token, appKey, provisioningRequestId, cmtEligible) {
  const options = {
    method: 'POST',
    credentials: getUPMCredentials(upm),
    headers: new window.Headers({
      'content-type': REMOTE_INSTALL_JSON
    })
  };
  return (await fetchOk("".concat(upm, "/rest/plugins/1.0/apps/install-subscribe?").concat(queryString.stringify({
    addonKey: appKey,
    token,
    provisioningRequestId,
    cmtEligible
  })), options)).json();
}
export const foldInstallableAddonIdentifier = addonIdentifier => (key, url, ari) => {
  if (addonIdentifier.key) {
    return key(addonIdentifier);
  } else if (addonIdentifier.url) {
    return url(addonIdentifier);
  } else if (addonIdentifier.ari) {
    return ari(addonIdentifier);
  } else {
    throw new Error('Invalid identifier');
  }
};
export const foldPermissionIdentifier = addonIdentifier => (key, ari) => {
  if (addonIdentifier.details.ecosystemCloudAppId) {
    return ari(addonIdentifier);
  } else {
    return key(addonIdentifier);
  }
};
export const calculateRetries = (maximumDurationInMinutes, pollAfter) => maximumDurationInMinutes * 60 * 1000 / pollAfter;
export async function waitForPendingUPMTask(upm, uri, pollAfter) {
  const options = {
    retries: calculateRetries(4.33, pollAfter),
    retryDelay: pollAfter,
    credentials: getUPMCredentials(upm),
    accept: response => response.json().then(bodyJSON => {
      var _bodyJSON$status;
      return (_bodyJSON$status = bodyJSON.status) !== null && _bodyJSON$status !== void 0 && _bodyJSON$status.done ? Promise.resolve() : Promise.reject(new Error('Request pending'));
    })
  };
  const response = await fetchOk(uri, options);
  const responseJSON = await response.json();
  const contentTypeHeader = response.headers.get('content-type');
  const headerHasInstallComplete = (contentTypeHeader === null || contentTypeHeader === void 0 ? void 0 : contentTypeHeader.indexOf(INSTALL_COMPLETE_JSON)) !== -1;
  if (headerHasInstallComplete) {
    return responseJSON;
  } else {
    return Promise.reject(_objectSpread(_objectSpread({}, responseJSON.status), {}, {
      contentTypeHeader
    }));
  }
}
export const toAddonDisplayDetailsFromMarketplaceApp = (marketplaceApp, environment) => {
  var _marketplaceApp$logo$;
  const {
    version,
    partner
  } = marketplaceApp;
  const links = linkTemplates({
    adminHubBaseUrl: environment.adminHubBaseUrl,
    mpacBaseUrl: environment.mpacBaseUrl,
    mpacCdnBaseUrl: environment.mpacCdnBaseUrl,
    macBaseUrl: environment.macBaseUrl,
    wacBaseUrl: environment.wacBaseUrl,
    atlassianCommunityBaseUrl: environment.atlassianCommunityBaseUrl
  });
  return _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
    key: marketplaceApp.appKey,
    name: marketplaceApp.name,
    origin: 'marketplace',
    description: marketplaceApp.tagline || ''
  }, setProp('version', version === null || version === void 0 ? void 0 : version.version)), setProp('vendor', partner === null || partner === void 0 ? void 0 : partner.name)), setProp('vendorLink', partner ? links.partnerDetails.expand({
    id: partner.id,
    slug: partner.slug
  }) : undefined)), setProp('icon', marketplaceApp.logo ? links.mediaFileProductListing.expand({
    imageId: ((_marketplaceApp$logo$ = marketplaceApp.logo.highResolution) === null || _marketplaceApp$logo$ === void 0 ? void 0 : _marketplaceApp$logo$.id) || marketplaceApp.logo.original.id
  }) : undefined)), setProp('privacyPolicy', marketplaceApp.privacyPolicyUrl)), setProp('eula', version === null || version === void 0 ? void 0 : version.endUserLicenseAgreementUrl)), {}, {
    vendorType: (partner === null || partner === void 0 ? void 0 : partner.partnerType) === 'ATLASSIAN_INTERNAL' ? 'internal' : 'thirdParty',
    metadata: undefined
  });
};
const resolveScopes = marketplaceApp => {
  var _marketplaceApp$versi;
  // let scopes;
  // if(marketplaceApp.version?.deployment) {
  //   {scopes} = (marketplaceApp.version?.deployment);
  // }
  const {
    scopes
  } = ((_marketplaceApp$versi = marketplaceApp.version) === null || _marketplaceApp$versi === void 0 ? void 0 : _marketplaceApp$versi.deployment) || {};
  return scopes ? scopes.map(a => a.id) : [];
};
export const toAddonDisplayDetailsFromUpmAvailablePlugin = upmAvailableApp => {
  var _upmAvailableApp$vend, _upmAvailableApp$vend2, _upmAvailableApp$logo;
  return _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
    key: upmAvailableApp.key,
    name: upmAvailableApp.name,
    origin: 'marketplace',
    description: upmAvailableApp.summary,
    version: upmAvailableApp.version
  }, setProp('vendor', (_upmAvailableApp$vend = upmAvailableApp.vendor) === null || _upmAvailableApp$vend === void 0 ? void 0 : _upmAvailableApp$vend.name)), setProp('vendorLink', (_upmAvailableApp$vend2 = upmAvailableApp.vendor) === null || _upmAvailableApp$vend2 === void 0 ? void 0 : _upmAvailableApp$vend2.marketplaceLink)), setProp('vendorType', upmAvailableApp.vendor ? upmAvailableApp.vendor.isAtlassian ? 'internal' : 'thirdParty' : undefined)), setProp('icon', (_upmAvailableApp$logo = upmAvailableApp.logo) === null || _upmAvailableApp$logo === void 0 ? void 0 : _upmAvailableApp$logo.link)), setProp('privacyPolicy', upmAvailableApp.links.privacy)), setProp('eula', upmAvailableApp.links.eula)), {}, {
    metadata: undefined
  });
};
export const toCloudMigrationTrialFromUpmAvailableApp = upmAvailableApp => ({
  isEligible: Boolean(upmAvailableApp.cmtEligible),
  btfSenEndDate: upmAvailableApp.btfSenEndDate ? new Date(upmAvailableApp.btfSenEndDate) : undefined
});
export const toInstallableAddonFromUpmAvailableApp = upmAvailableApp => ({
  details: toAddonDisplayDetailsFromUpmAvailablePlugin(upmAvailableApp),
  preinstalled: upmAvailableApp.preinstalled,
  scopes: (upmAvailableApp.permissions || []).map(p => p.key),
  metadata: undefined,
  eligibleForFreeOnSandbox: upmAvailableApp.eligibleForFreeOnSandbox,
  cloudMigrationTrial: toCloudMigrationTrialFromUpmAvailableApp(upmAvailableApp),
  parentCapabilitySet: upmAvailableApp.parentCapabilitySet
});
export function setProp(prop, value) {
  return value !== undefined && value !== null ? {
    [prop]: value
  } : {};
}
export function toInstalledAddonFromMarketplaceApp(marketplaceApp, environment, getStartedUrl, configureUrl, manageUrl, paymentModel, capabilitySet) {
  return _objectSpread(_objectSpread(_objectSpread(_objectSpread({
    details: toAddonDisplayDetailsFromMarketplaceApp(marketplaceApp, environment),
    scopes: resolveScopes(marketplaceApp),
    enabled: true,
    latestVersion: true,
    installationId: marketplaceApp.appKey
  }, setProp('getStartedUrl', getStartedUrl)), setProp('configureUrl', configureUrl)), setProp('manageAppUrl', manageUrl)), {}, {
    paymentModel,
    installTime: new Date(),
    authType: {
      type: '2LO'
    },
    metadata: undefined,
    capabilitySet
  });
}
export function toInstalledAddon(marketplaceApp, upmInstalledApp, installedMarketplacePlugin, environment) {
  var _upmInstalledApp$link, _upmInstalledApp$link2, _upmInstalledApp$link3;
  const getStartedUrl = (_upmInstalledApp$link = upmInstalledApp.links) === null || _upmInstalledApp$link === void 0 ? void 0 : _upmInstalledApp$link.postInstall;
  const configureUrl = (_upmInstalledApp$link2 = upmInstalledApp.links) === null || _upmInstalledApp$link2 === void 0 ? void 0 : _upmInstalledApp$link2.configure;
  const manageAppUrl = (_upmInstalledApp$link3 = upmInstalledApp.links) === null || _upmInstalledApp$link3 === void 0 ? void 0 : _upmInstalledApp$link3.manage;
  return getPaymentModel(installedMarketplacePlugin, marketplaceApp).map(spiPaymentModel => toInstalledAddonFromMarketplaceApp(marketplaceApp, environment, getStartedUrl, configureUrl, manageAppUrl, spiPaymentModel, upmInstalledApp.capabilitySet));
}
export const FreeSPIPaymentModel = {
  type: 'free'
};
export const PaidSPIPaymentModel = sr => ({
  type: 'paid',
  licenseType: sr
});
const getEvaluationLicenseType = (expiry, isAutoRenewal) => expiry != null ? Either.right({
  type: 'evaluation',
  expiry,
  isAutoRenewal
}) : Either.left('Unable to get Expiry date for evaluation license');
const getSubscriptionLicenseType = (endDate, isAutoRenewal) => isAutoRenewal ? Either.right({
  type: 'subscription',
  renewalType: {
    isAutoRenewal: true
  }
}) : endDate != null ? Either.right({
  type: 'subscription',
  renewalType: {
    isAutoRenewal: false,
    endDate
  }
}) : Either.left('Unable to get end date for subscription license');
export const getPaymentModel = (imp, mp) => {
  var _mp$version;
  const paymentModel = (_mp$version = mp.version) === null || _mp$version === void 0 ? void 0 : _mp$version.paymentModel;
  if (!paymentModel) {
    return Either.left('Unable to get payment model from marketplace');
  }
  return foldPaymentModel(paymentModel)(() => Either.right(FreeSPIPaymentModel), () => {
    var _imp$licenseDetails, _imp$licenseDetails2, _imp$licenseDetails3;
    const evaluation = (_imp$licenseDetails = imp.licenseDetails) === null || _imp$licenseDetails === void 0 ? void 0 : _imp$licenseDetails.evaluation;
    const autoRenewal = (_imp$licenseDetails2 = imp.licenseDetails) === null || _imp$licenseDetails2 === void 0 ? void 0 : _imp$licenseDetails2.autoRenewal;
    const expiryDate = (_imp$licenseDetails3 = imp.licenseDetails) === null || _imp$licenseDetails3 === void 0 ? void 0 : _imp$licenseDetails3.expiryDate;
    if (evaluation != null && autoRenewal != null) {
      var _imp$licenseDetails4;
      return evaluation ? getEvaluationLicenseType(expiryDate, autoRenewal).map(PaidSPIPaymentModel) : getSubscriptionLicenseType((_imp$licenseDetails4 = imp.licenseDetails) === null || _imp$licenseDetails4 === void 0 ? void 0 : _imp$licenseDetails4.subscriptionEndDate, autoRenewal).map(PaidSPIPaymentModel);
    } else {
      return Either.left('Unable to get subscription information from UPM');
    }
  }, () => Either.left('Cannot get subscription information for a PvV app'));
};
export function getUPMCredentials(upmBaseUrl) {
  return upmBaseUrl === '' ? 'same-origin' : 'include';
}
export const arrayToMap = (array, getKeyFunction) => array.reduce((acc, value) => {
  acc[getKeyFunction(value)] = value;
  return acc;
}, {});
export const transformToPaymentModel = (version, marketplaceInstallation) => {
  var _marketplaceInstallat, _marketplaceInstallat2;
  let type = {
    type: 'free'
  };
  const trialEndDate = (_marketplaceInstallat = marketplaceInstallation.license) === null || _marketplaceInstallat === void 0 ? void 0 : _marketplaceInstallat.trialEndDate;
  const isEvaluation = (_marketplaceInstallat2 = marketplaceInstallation.license) === null || _marketplaceInstallat2 === void 0 ? void 0 : _marketplaceInstallat2.isEvaluation;
  if (version.paymentModel === 'PAID_VIA_ATLASSIAN') {
    if (isEvaluation && trialEndDate) {
      type = {
        type: 'paid',
        licenseType: {
          type: 'evaluation',
          expiry: Date.parse(trialEndDate),
          isAutoRenewal: true
        }
      };
      return type;
    }
    type = {
      type: 'paid',
      licenseType: {
        type: 'subscription',
        renewalType: {
          isAutoRenewal: true
        }
      }
    };
  }
  return type;
};